<template>
  <div class="sd">
    <div v-if="loading" class="sd-pad"><SkeletonList :rows="3" label="Loading feedback…" /></div>
    <div v-else-if="loadError" class="sd-pad">
      <div class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i><span>{{ loadError }}</span></div>
      <button v-if="!missing" type="button" class="pb-button outline" @click="load">Retry</button>
    </div>

    <template v-else-if="item">
      <div class="sd-head">
        <div class="sd-head-text">
          <div class="pb-row sd-chips">
            <StatusChip :status="item.status" />
            <!-- The permission chip, unless it would only repeat "Private". -->
            <StatusChip v-if="item.status !== 'private' || item.may_publish" :consent="item.may_publish" />
            <span v-if="item.origin !== 'form'" class="pb-chip is-info"><i class="mdi mdi-import pb-ico" aria-hidden="true"></i>Imported</span>
          </div>
          <h2 ref="title" tabindex="-1" class="sd-title display-font">{{ item.name || 'Anonymous' }}</h2>
          <p class="pb-muted">{{ subline }}</p>
        </div>
        <button type="button" class="pb-icon-button" aria-label="Close" @click="$emit('close')"><i class="mdi mdi-close" aria-hidden="true"></i></button>
      </div>

      <div class="sd-actions" role="group" aria-label="Actions">
        <LockedAction v-if="allowed('publish') && publishCap" feature="published" look="pb-button" icon="mdi-earth" label="Publish" :cap="publishCap" :used="publishedCount" />
        <button v-else-if="allowed('publish')" type="button" class="pb-button" :disabled="busy" @click="act('publish')">
          <i class="mdi mdi-earth pb-ico" aria-hidden="true"></i>Publish</button>
        <!-- Shown but disabled, with the reason read out, when this feedback can't be published (yet). -->
        <button v-else-if="publishBlocked" type="button" class="pb-button" disabled :aria-describedby="'sd-why-' + item.id">
          <i class="mdi mdi-earth pb-ico" aria-hidden="true"></i>Publish</button>
        <button v-if="allowed('approve')" type="button" :class="['pb-button', allowed('publish') ? 'outline' : '']" :disabled="busy" @click="act('approve')">
          <i class="mdi mdi-check pb-ico" aria-hidden="true"></i>{{ item.brand.requires_client_approval && item.client_decision !== 'approved' ? 'Approve and send to client' : 'Approve' }}</button>
        <button v-if="allowed('request_client') && !(allowed('approve') && item.brand.requires_client_approval)" type="button" class="pb-button outline" :disabled="busy" @click="act('request_client')">
          <i class="mdi mdi-account-check-outline pb-ico" aria-hidden="true"></i>Ask client</button>
        <button v-if="allowed('unpublish')" type="button" class="pb-button outline" :disabled="busy" @click="act('unpublish')">
          <i class="mdi mdi-eye-off-outline pb-ico" aria-hidden="true"></i>Unpublish</button>
        <button v-if="allowed('restore')" type="button" class="pb-button outline" :disabled="busy" @click="act('restore')">Restore</button>
        <button v-if="allowed('not_spam')" type="button" class="pb-button outline" :disabled="busy" @click="act('not_spam')">Not spam</button>
        <details v-if="moreActions.length" class="sd-more">
          <summary class="pb-button ghost"><i class="mdi mdi-dots-horizontal pb-ico" aria-hidden="true"></i>More</summary>
          <div class="sd-menu">
            <button v-for="action in moreActions" :key="action.key" type="button" :class="['sd-menu-item', action.tone]" :disabled="busy" @click="act(action.key)">
              <i :class="['mdi', action.icon]" aria-hidden="true"></i>{{ action.label }}</button>
          </div>
        </details>
      </div>
      <p v-if="publishBlocked" :id="'sd-why-' + item.id" class="pb-note sd-why"><i class="mdi mdi-information-outline pb-ico" aria-hidden="true"></i><span>{{ publishBlocked }}</span></p>
      <p v-if="needsReviewer" class="pb-note sd-why"><i class="mdi mdi-account-plus-outline pb-ico" aria-hidden="true"></i>
        <span>Add your client as a reviewer on <router-link to="/team">Team &amp; approvals</router-link> so they get a link to approve it.</span></p>
      <div v-if="actionError" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i><span>{{ actionError }}</span></div>

      <section class="sd-section" aria-labelledby="sd-words">
        <h3 id="sd-words" class="sd-h">What they wrote</h3>
        <div v-for="answer in item.answers" :key="answer.question_id" class="sd-answer">
          <p class="sd-q">{{ answer.label }}</p>
          <blockquote class="sd-a">{{ answer.text }}</blockquote>
        </div>
        <div v-if="item.rating" class="pb-stars sd-rating" role="img" :aria-label="'Rated ' + item.rating + ' out of 5'">
          <i v-for="n in 5" :key="n" :class="['mdi', 'mdi-star', { off: n > item.rating }]" aria-hidden="true"></i>
        </div>
        <dl class="sd-details">
          <template v-if="item.job_title"><dt>Job title</dt><dd>{{ item.job_title }}</dd></template>
          <template v-if="item.company"><dt>Company</dt><dd>{{ item.company }}</dd></template>
          <template v-if="item.contact_email"><dt>Email <span class="pb-muted">(private)</span></dt><dd><a :href="'mailto:' + item.contact_email">{{ item.contact_email }}</a></dd></template>
          <template v-if="item.photo"><dt>Photo</dt><dd>
            <img v-if="photoSrc" :src="photoSrc" alt="Photo the customer added" class="sd-photo">
            <span v-else-if="item.photo.status === 'failed'" class="pb-muted">The photo couldn't be read.</span>
            <span v-else class="pb-muted">Processing…</span></dd></template>
          <template v-if="item.source_label"><dt>Source</dt><dd>{{ item.source_label }}</dd></template>
        </dl>
        <p class="pb-muted sd-private-note">Contact details and notes are private: they never appear on public pages.</p>
      </section>

      <section class="sd-section" aria-labelledby="sd-consent">
        <h3 id="sd-consent" class="sd-h">Permission</h3>
        <ConsentSummary :consent="item.consent" :withdrawn-at="item.withdrawn_at" :origin="item.origin" :attestation="item.attestation" :source-label="item.source_label"
                        :contact-email="item.contact_email || ''" />
        <div v-if="item.client_decision" :class="['pb-alert', item.client_decision === 'approved' ? 'is-success' : 'is-info']">
          <i :class="['mdi', item.client_decision === 'approved' ? 'mdi-check-decagram-outline' : 'mdi-comment-alert-outline', 'pb-ico']" aria-hidden="true"></i>
          <span><strong>{{ item.client_decision === 'approved' ? 'Approved by your client' : 'Your client asked for changes' }}</strong>
            <template v-if="item.client_reviewer"> ({{ item.client_reviewer }})</template><template v-if="item.client_decided_at">, {{ formatDateTime(item.client_decided_at) }}</template>.
            <template v-if="item.client_comment"><br>"{{ item.client_comment }}"</template></span>
        </div>
        <div v-if="item.spam_flags.length" class="pb-alert"><i class="mdi mdi-alert-octagon-outline pb-ico" aria-hidden="true"></i>
          <span>Looks automated: {{ item.spam_flags.map(flagText).join(', ') }}. It stays here until you decide.</span></div>
      </section>

      <section v-if="item.may_publish" class="sd-section" aria-labelledby="sd-quote">
        <h3 id="sd-quote" class="sd-h">The public quote</h3>
        <fieldset v-if="textAnswers.length > 1" class="sd-pick">
          <legend class="pb-label">Answers to quote</legend>
          <label v-for="answer in textAnswers" :key="answer.question_id" class="pb-check">
            <input v-model="quoteIds" type="checkbox" :value="answer.question_id" @change="excerpt = null">{{ answer.label }}
          </label>
        </fieldset>
        <ExcerptEditor v-if="quoteIds.length" v-model="excerpt" :original="chosenText" @valid="excerptValid = $event" />
        <p class="pb-label sd-preview-label">How it will look</p>
        <TestimonialCard :item="previewItem" />
        <div class="pb-actions sd-save">
          <button type="button" class="pb-button" :disabled="!quoteChanged || !excerptValid || !quoteIds.length || saving" @click="saveQuote">
            {{ saving ? 'Saving…' : 'Save quote' }}</button>
          <span v-if="quoteChanged" class="pb-muted">Unsaved changes</span>
        </div>
      </section>

      <section class="sd-section" aria-labelledby="sd-tags">
        <h3 id="sd-tags" class="sd-h">Tags</h3>
        <div class="pb-row sd-tags">
          <span v-for="tag in item.tags" :key="tag" class="pb-chip">{{ tag }}
            <button type="button" class="sd-tag-x" :aria-label="'Remove tag ' + tag" @click="removeTag(tag)"><i class="mdi mdi-close" aria-hidden="true"></i></button></span>
          <form v-if="item.tags.length < 10" class="sd-tag-form" @submit.prevent="addTag">
            <label class="pb-sr-only" :for="'tag-' + item.id">Add a tag</label>
            <input :id="'tag-' + item.id" v-model="newTag" class="pb-input sd-tag-input" maxlength="30" placeholder="Add a tag">
            <button type="submit" class="pb-button sm tonal" :disabled="!newTag.trim()">Add</button>
          </form>
        </div>
      </section>

      <section class="sd-section" aria-labelledby="sd-notes">
        <h3 id="sd-notes" class="sd-h">Private notes</h3>
        <ul v-if="item.notes.length" class="sd-notes">
          <li v-for="note in item.notes" :key="note.id"><p>{{ note.body }}</p><span class="pb-muted">{{ note.author }} · {{ formatDateTime(note.at) }}</span></li>
        </ul>
        <form class="sd-note-form" @submit.prevent="addNote">
          <label class="pb-sr-only" :for="'note-' + item.id">Write a private note</label>
          <textarea :id="'note-' + item.id" v-model="note" class="pb-textarea" rows="2" maxlength="2000" placeholder="Only your team sees notes"></textarea>
          <button type="submit" class="pb-button sm outline" :disabled="!note.trim() || saving">Add note</button>
        </form>
      </section>

      <section class="sd-section" aria-labelledby="sd-history">
        <h3 id="sd-history" class="sd-h">History</h3>
        <ol class="sd-history">
          <li v-for="event in item.events" :key="event.id"><span class="sd-hist-dot" aria-hidden="true"></span>
            <span><strong>{{ describe(event) }}</strong><span class="pb-muted">{{ actor(event) }} · {{ formatDateTime(event.at) }}</span></span></li>
        </ol>
      </section>
    </template>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import ConsentSummary from './ConsentSummary.vue';
import LockedAction from './LockedAction.vue';
import ExcerptEditor from './ExcerptEditor.vue';
import SkeletonList from './SkeletonList.vue';
import StatusChip from './StatusChip.vue';
import TestimonialCard from './TestimonialCard.vue';
import UpgradeDialog from './UpgradeDialog.vue';
import { get, patch, post, message, planLimit, privateImage } from '../api';
import { capText } from '../lib/plans';
import { actorOf, describeEvent } from '../lib/events';
import { formatDateTime } from '../lib/format';
import { publishedToast } from '../lib/showing';
import { toast } from '../lib/toast';

const DONE = {
  approve: 'Approved.', request_client: 'Sent to your client for approval.',
  unpublish: 'Unpublished. It leaves your widget and wall within a minute.', archive: 'Archived.', restore: 'Restored.',
  spam: 'Moved to spam.', not_spam: 'Moved back to the inbox.', mark_private: 'Kept private. It can\'t be published.'
};
const FLAGS = { honeypot: 'a hidden field was filled', links: 'many links', pattern: 'shouting or repeated characters', duplicate: 'a duplicate of earlier feedback' };

export default {
  name: 'SubmissionDetail',
  components: { ConsentSummary, ExcerptEditor, LockedAction, SkeletonList, StatusChip, TestimonialCard, UpgradeDialog },
  props: { id: { type: Number, required: true } },
  data: () => ({ item: null, loading: true, loadError: '', missing: false, busy: false, saving: false, actionError: '', photoSrc: '',
    quoteIds: [], excerpt: null, excerptValid: true, newTag: '', note: '', upgrade: null, upgradeOpen: false }),
  computed: {
    publishedCount() { return this.$store.getters['praiseboard/usage'].published || 0; },
    // At the plan's cap, with a plan above that publishes more: Publish shows the cap and explains, before the API refuses.
    publishCap() {
      const allowed = this.$store.getters['praiseboard/limits'].published;
      if (typeof allowed !== 'number' || this.publishedCount < allowed || !this.$store.getters['praiseboard/availableOn']('published')) return '';
      const data = this.$store.getters['praiseboard/data'];
      return capText(this.publishedCount, allowed, (data && data.plan_label) || '');
    },
    subline() {
      if (!this.item) return '';
      const parts = [formatDateTime(this.item.created_at)];
      if (this.item.form) parts.push(this.item.form.name);
      if (this.$store.getters['praiseboard/activeBrands'].length > 1) parts.push(this.item.brand.name);
      return parts.join(' · ');
    },
    textAnswers() { return (this.item ? this.item.answers : []).filter(answer => (answer.text || '').trim()); },
    chosenText() {
      return this.textAnswers.filter(answer => this.quoteIds.indexOf(answer.question_id) >= 0).map(answer => answer.text.trim()).join('\n\n');
    },
    quoteChanged() {
      if (!this.item) return false;
      const saved = this.item.quote || {};
      const ids = (saved.answer_ids || []).slice().sort().join('|');
      return ids !== this.quoteIds.slice().sort().join('|') || (saved.excerpt || null) !== (this.excerpt || null);
    },
    previewItem() {
      const preview = Object.assign({}, this.item.public_preview);
      preview.text = this.excerpt || this.chosenText || preview.text;
      preview.photo_url = this.item.consent.show_photo ? this.photoSrc : '';
      preview.photo = null;
      return preview;
    },
    moreActions() {
      const list = [];
      if (this.allowed('mark_private')) list.push({ key: 'mark_private', label: 'Keep private', icon: 'mdi-lock-outline' });
      if (this.allowed('archive')) list.push({ key: 'archive', label: 'Archive', icon: 'mdi-archive-outline' });
      if (this.allowed('spam')) list.push({ key: 'spam', label: 'Mark as spam', icon: 'mdi-alert-octagon-outline', tone: 'danger' });
      return list;
    },
    publishBlocked() {
      const item = this.item;
      if (!item || item.status === 'published' || ['archived', 'spam'].indexOf(item.status) >= 0) return '';
      if (item.withdrawn_at) return 'The customer withdrew their permission, so this can\'t be published.';
      if (!item.may_publish) return 'This customer chose to keep their feedback private, so it can\'t be published.';
      if (item.status === 'awaiting_client') return 'Waiting for your client\'s approval before it can be published.';
      if (item.brand.requires_client_approval && item.client_decision !== 'approved') return 'This brand needs your client\'s approval before publishing.';
      return '';
    },
    needsReviewer() {
      return this.item && this.item.status === 'awaiting_client' && !this.item.reviewers;
    }
  },
  watch: { id() { this.load(); } },
  mounted() { this.load(); },
  methods: {
    formatDateTime,
    describe: describeEvent,
    actor: actorOf,
    flagText(flag) { return FLAGS[flag] || flag; },
    allowed(action) { return !!(this.item && this.item.allowed_actions.indexOf(action) >= 0); },
    take(item) {
      this.item = item;
      this.quoteIds = (item.quote && item.quote.answer_ids) || [];
      this.excerpt = (item.quote && item.quote.excerpt) || null;
      this.$emit('changed', item);
      this.photoSrc = '';
      if (item.photo && item.photo.status === 'complete') {
        privateImage(item.photo.id).then(url => { if (this.item && this.item.id === item.id) this.photoSrc = url; }).catch(() => {});
      }
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      this.actionError = '';
      try {
        const item = await get('/submissions/' + this.id);
        this.take(item);
        if (item.status === 'new') this.take(await post('/submissions/' + this.id + '/actions', { action: 'mark_read' }));
        this.$nextTick(() => { if (this.$refs.title) this.$refs.title.focus(); });
      } catch (error) {
        // Gone, or on a brand this person can't see: say so plainly, without a Retry that can't help.
        this.missing = !!(error.response && error.response.status === 404);
        this.loadError = this.missing ? "This feedback isn't available. It may have been deleted, or it belongs to a brand you can't see."
          : message(error, "We couldn't load this feedback. Check your connection and try again.");
      } finally {
        this.loading = false;
      }
    },
    async act(action) {
      this.busy = true;
      this.actionError = '';
      try {
        this.take(await post('/submissions/' + this.id + '/actions', { action }));
        if (action === 'publish') {
          // "Live" only where a widget or the wall shows it; otherwise the next step.
          const brands = this.$store.getters['praiseboard/brands'];
          const done = publishedToast(brands.find(brand => this.item.brand && brand.id === this.item.brand.id), 1);
          toast(done.text, 'success', done.action);
        } else {
          // On a brand that needs client approval, approving sends it to the client.
          toast(action === 'approve' && this.item.status === 'awaiting_client' ? DONE.request_client : (DONE[action] || 'Done.'));
        }
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else this.actionError = message(error);
      } finally {
        this.busy = false;
        const open = this.$el.querySelector('.sd-more[open]');
        if (open) open.removeAttribute('open');
      }
    },
    async saveQuote() {
      this.saving = true;
      this.actionError = '';
      try {
        this.take(await patch('/submissions/' + this.id, { quote: { answer_ids: this.quoteIds, excerpt: this.excerpt } }));
        toast('Quote saved.');
      } catch (error) { this.actionError = message(error); } finally { this.saving = false; }
    },
    async saveTags(tags) {
      try { this.take(await patch('/submissions/' + this.id, { tags })); } catch (error) { this.actionError = message(error); }
    },
    addTag() {
      const tag = this.newTag.trim();
      if (!tag) return;
      this.newTag = '';
      this.saveTags(this.item.tags.concat([tag]));
    },
    removeTag(tag) { this.saveTags(this.item.tags.filter(item => item !== tag)); },
    async addNote() {
      this.saving = true;
      try {
        this.take(await post('/submissions/' + this.id + '/notes', { body: this.note.trim() }));
        this.note = '';
      } catch (error) { this.actionError = message(error); } finally { this.saving = false; }
    }
  }
};
</script>

<style scoped>
.sd{display:flex;flex-direction:column;gap:0}
.sd-pad{padding:24px}
.sd-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:22px 24px 14px}
.sd-head-text{min-width:0}
.sd-chips{flex-wrap:wrap;gap:6px;margin-bottom:10px}
.sd-title{font:500 26px/1.2 var(--font-display)!important;color:#1F2328;margin:0;outline:none}
.sd-actions{display:flex;flex-wrap:wrap;gap:8px;padding:0 24px 16px;border-bottom:1px solid #E6DED2}
.sd-why{margin:12px 24px 0}
.sd>.pb-alert{margin:12px 24px 0}
.sd-section{padding:20px 24px;border-bottom:1px solid #EFE9E0}
.sd-section:last-of-type{border-bottom:0}
.sd-h{font:700 12px/1.2 var(--font-body);letter-spacing:.1em;text-transform:uppercase;color:#6B635B;margin:0 0 14px}
.sd-answer{margin-bottom:14px}
.sd-q{margin:0 0 6px;font:600 13.5px/1.4 var(--font-body);color:#4F4A45}
.sd-a{margin:0;padding:12px 14px;border-left:3px solid #B4472B;background:#FBF8F3;border-radius:0 10px 10px 0;
  font:400 15.5px/1.6 var(--font-body);color:#1F2328;white-space:pre-line;overflow-wrap:anywhere}
.sd-rating{margin:4px 0 12px}
.sd-details{display:grid;grid-template-columns:auto 1fr;gap:6px 16px;margin:8px 0 0;font-size:14.5px}
.sd-details dt{color:#6B635B}
.sd-details dd{margin:0;color:#1F2328;overflow-wrap:anywhere}
.sd-photo{width:72px;height:72px;border-radius:12px;object-fit:cover;border:1px solid #E6DED2}
.sd-private-note{margin-top:12px}
.sd-section .pb-alert{margin:12px 0 0}
.sd-pick{border:0;margin:0 0 14px;padding:0;display:grid;gap:6px}
.sd-preview-label{margin:16px 0 8px}
.sd-save{margin-top:14px}
.sd-tags{flex-wrap:wrap;gap:8px}
.sd-tag-x{display:inline-grid;place-items:center;width:24px;height:24px;margin-right:-6px;border:0;border-radius:50%;background:transparent;color:#4F4A45;cursor:pointer}
.sd-tag-x:hover{background:#E6DED2}
.sd-tag-form{display:flex;gap:6px;align-items:center}
.sd-tag-input{width:150px;padding:7px 10px;min-height:36px}
.sd-notes{list-style:none;margin:0 0 12px;padding:0;display:grid;gap:10px}
.sd-notes li{padding:10px 12px;background:#FBF0DF;border-radius:10px}
.sd-notes p{margin:0 0 4px;font-size:14.5px;color:#1F2328;white-space:pre-line}
.sd-note-form{display:grid;gap:8px;justify-items:start}
.sd-history{list-style:none;margin:0;padding:0;display:grid;gap:10px}
.sd-history li{display:flex;gap:10px;align-items:flex-start;font-size:14px}
.sd-history li>span:last-child{display:flex;flex-direction:column}
.sd-history strong{font-weight:600;color:#1F2328}
.sd-hist-dot{width:8px;height:8px;margin-top:7px;border-radius:50%;background:#D4C9B8;flex:0 0 8px}
.sd-more{position:relative}
.sd-more summary{list-style:none}
.sd-more summary::-webkit-details-marker{display:none}
.sd-menu{position:absolute;z-index:5;top:calc(100% + 6px);left:0;min-width:200px;padding:6px;background:#fff;border:1px solid #E6DED2;border-radius:12px;box-shadow:var(--pb-shadow-raised)}
.sd-menu-item{display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:8px 12px;border:0;border-radius:8px;background:none;
  font:500 14px var(--font-body);color:#1F2328;cursor:pointer;text-align:left}
.sd-menu-item:hover{background:#F3EEE6}
.sd-menu-item.danger{color:#B42318}
@media(max-width:600px){
  .sd-head,.sd-section{padding-left:16px;padding-right:16px}
  .sd-actions{padding:0 16px 14px}
  .sd-actions>.pb-button{flex:1 1 auto}
  .sd-why,.sd>.pb-alert{margin-left:16px;margin-right:16px}
  .sd-menu{left:auto;right:0}
}
</style>
